import fulfill from '../../fulfill';
import { CORE_API_HOSTNAME } from '../../env';
import { SideEffectFunction } from '../../sideEffects';
import {
    ActionTypes,
    FetchDiscount,
    UpdateDiscount,
    UpdateDiscountResponse,
    DeleteDiscount,
    DeleteDiscountResponse,
    FetchDiscountCustomers,
    FetchDiscountCustomersResponse,
    FetchDiscountResponse,
    CreateAsset,
    CreateAssetResponse,
    UploadAssetResponse
} from './actions';
import { CustomerRef, Discount } from '../../types/discounts';
import { AssetResponseObject } from '../types';

export const fetchDiscount: SideEffectFunction<FetchDiscount, FetchDiscountResponse> = async (action) => {
    const { accountId, discountId } = action.payload;
    const result: FetchDiscountResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}`,
        handlers: {
            200: (discount: Discount) => ({
                type: ActionTypes.FetchDiscountResponse,
                payload: discount,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const fetchDiscountCustomers: SideEffectFunction<
    FetchDiscountCustomers,
    FetchDiscountCustomersResponse
> = async (action) => {
    const { accountId, discountId } = action.payload;
    const result: FetchDiscountCustomersResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}/customers`,
        handlers: {
            200: (discountCustomers: CustomerRef[]) => ({
                type: ActionTypes.FetchDiscountCustomersResponse,
                payload: discountCustomers,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const updateDiscount: SideEffectFunction<UpdateDiscount, UpdateDiscountResponse> = async (action) => {
    const { accountId, discountId, ...json } = action.payload;
    const result: UpdateDiscountResponse = await fulfill.put({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}`,
        json: json.discount,
        handlers: {
            200: (discount: Discount) => ({
                type: ActionTypes.UpdateDiscountResponse,
                payload: discount,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const deleteDiscount: SideEffectFunction<DeleteDiscount, DeleteDiscountResponse> = async (action) => {
    const { accountId, discountId } = action.payload;
    const result: DeleteDiscountResponse = await fulfill.delete({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/discounts/rules/${discountId}`,
        handlers: {
            200: () => ({
                type: ActionTypes.DeleteDiscountResponse,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const createAsset: SideEffectFunction<CreateAsset, CreateAssetResponse> = async (action) => {
    const { accountId, asset } = action.payload;
    const result: CreateAssetResponse = await fulfill.post({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/assets`,
        json: {
            size_bytes: asset.file.size,
            mime_type: asset.file.type,
        },
        handlers: {
            200: (assetDetails: AssetResponseObject) => ({
                type: ActionTypes.CreateAssetResponse,
                payload: {
                    accountId,
                    uploadUrl: assetDetails.upload_url,
                    publicUrl: assetDetails.public_url,
                    type: asset.file.type,
                    size: asset.file.size,
                    file: asset.file,
                    discountId: asset.discountId,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

export const uploadAsset: SideEffectFunction<CreateAssetResponse, UploadAssetResponse> = async (action) => {
    const { accountId, uploadUrl, file } = action.payload;
    const result: UploadAssetResponse = await fulfill.put({
        accountId,
        noAuthentication: true,
        url: uploadUrl,
        body: file,
        handlers: {
            200: () => ({
                type: ActionTypes.UploadAssetResponse,
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const effects = {
    [ActionTypes.FetchDiscount]: fetchDiscount,
    [ActionTypes.UpdateDiscount]: updateDiscount,
    [ActionTypes.DeleteDiscount]: deleteDiscount,
    [ActionTypes.FetchDiscountCustomers]: fetchDiscountCustomers,
    [ActionTypes.CreateAsset]: createAsset,
    [ActionTypes.CreateAssetResponse]: uploadAsset,
};

export default effects;
